/**
 * RailCards — War Room rail card components for case-path visualization.
 *
 * Three presentational cards:
 *   - NextBestActionCard: Shows recommended action with dollar impact
 *   - CaseStrengthMeter: Shows readiness score with blocker awareness
 *   - DefensePreviewCard: Shows Daubert score and top defense attacks
 *
 * Design Law: All colors use CSS var tokens (no raw hex literals).
 * Reuse Law: Uses VerifiedValue and StateChip from shared components.
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { VerifiedValue } from '../shared/VerifiedValue';
import { StateChip } from '../shared/StateChip';

// =============================================================================
// NextBestActionCard
// =============================================================================

interface NextBestActionCardProps {
  /** Label describing the recommended action */
  action_label: string;
  /** Dollar impact of the action (rendered via VerifiedValue) */
  dollar_impact: number;
  /** ID of the engine that generated this recommendation */
  engine_id: string;
  /** Callback when the Go button is clicked */
  onGo: () => void;
}

/**
 * Renders a next-best-action card with:
 * - Action label
 * - Dollar impact via VerifiedValue (provenance='E' for engine)
 * - Engine ID
 * - Go button wired to onGo
 */
export function NextBestActionCard({
  action_label,
  dollar_impact,
  engine_id,
  onGo,
}: NextBestActionCardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        'bg-[var(--color-bg-elevated)] border-[var(--color-hairline)]'
      )}
      data-testid="next-best-action-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3
            className="text-sm font-medium text-[var(--color-text-primary)]"
            data-testid="action-label"
          >
            {action_label}
          </h3>
          <p
            className="mt-1 text-xs text-[var(--color-text-secondary)]"
            data-testid="engine-id"
          >
            {engine_id}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <VerifiedValue
            value={dollar_impact}
            provenance="E"
            status="fresh"
            size="md"
            data-testid="dollar-impact"
          />
          <button
            type="button"
            onClick={onGo}
            className={cn(
              'px-4 py-1.5 rounded-md text-sm font-medium',
              'bg-[var(--color-brand)] text-[var(--color-bg-base)]',
              'hover:opacity-90 transition-opacity'
            )}
            data-testid="go-button"
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// CaseStrengthMeter
// =============================================================================

interface CaseStrengthMeterProps {
  /** Readiness score from 0-100 */
  score0to100: number;
  /** Number of blockers (triggers blocker state when > 0) */
  blockers: number;
  /** Evidence completeness percentage */
  evidencePct: number;
}

/**
 * Renders a case strength meter with:
 * - Progress bar showing score0to100 as fill percentage
 * - StateChip label (blocker state when blockers > 0)
 * - Evidence percentage display
 */
export function CaseStrengthMeter({
  score0to100,
  blockers,
  evidencePct,
}: CaseStrengthMeterProps) {
  const hasBlockers = blockers > 0;

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        'bg-[var(--color-bg-elevated)] border-[var(--color-hairline)]'
      )}
      data-testid="case-strength-meter"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
          Case Readiness
        </h3>
        <span data-testid="readiness-chip">
          <StateChip
            state={hasBlockers ? 'blocker' : 'verified'}
            text={hasBlockers ? `${blockers} Blocker${blockers !== 1 ? 's' : ''}` : 'Ready'}
            size="sm"
          />
        </span>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div
          className={cn(
            'h-2 w-full rounded-full',
            'bg-[var(--color-bg-base)]'
          )}
        >
          <div
            className={cn(
              // No mount animation (2026-07-25): decorative width transition removed per the design
              // law's no-celebration-motion rule; it was also the only in-code suspect for a one-off
              // paint artifact in the owner's screenshot that never reproduced in harness renders.
              'h-full rounded-full',
              hasBlockers
                ? 'bg-[var(--color-blocker)]'
                : 'bg-[var(--color-verified,var(--color-text-secondary))]'
            )}
            style={{ width: `${Math.max(0, Math.min(100, score0to100))}%` }}
            data-testid="readiness-bar"
          />
        </div>
      </div>

      {/* Evidence percentage */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-[var(--color-text-secondary)]">Evidence</span>
        <span
          className="font-mono text-[var(--color-text-primary)]"
          data-testid="evidence-pct"
        >
          {evidencePct}%
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// DefensePreviewCard
// =============================================================================

interface AttackItem {
  /** Methodology being attacked */
  methodology: string;
  /** Description of the attack */
  attack: string;
  our_counter?: string | null;
  counter_gap?: boolean;
  evidence_needed?: { requirement_key: string; display_name: string; document_type: string }[];
  impact_if_collected?: string | null;
  collection_status?: { state: string; requested_at?: string } | null;
}

interface DefensePreviewCardProps {
  /** Daubert reliability score (0-100) */
  daubert_score_0_100: number;
  /** List of top defense attacks */
  top_attacks: AttackItem[];
  /** Document detection status from the brief (fail-closed: absent → unavailable) */
  document_detection?: string | null;
}

/**
 * Renders a defense preview card with:
 * - Daubert score display
 * - List of top attacks with blocker-tint framing
 * - Empty state message when top_attacks is empty array
 */
export function DefensePreviewCard({
  daubert_score_0_100,
  top_attacks,
  document_detection,
}: DefensePreviewCardProps) {
  const hasAttacks = top_attacks.length > 0;
  const displayScore = Math.round(Math.min(100, Math.max(0, daubert_score_0_100)));
  const docDetection = document_detection ?? 'unavailable';

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        'bg-[var(--color-bg-elevated)] border-[var(--color-hairline)]'
      )}
      data-testid="defense-preview-card"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
          Defense Preview
        </h3>
        <span
          className={cn(
            'font-mono text-sm',
            displayScore < 50
              ? 'text-[var(--color-blocker)]'
              : 'text-[var(--color-text-secondary)]'
          )}
          data-testid="daubert-score"
        >
          {displayScore}/100
        </span>
      </div>

      {/* Attacks list or empty state */}
      <div className="space-y-2">
        {hasAttacks ? (
          top_attacks.map((item, index) => (
            <div
              key={index}
              className={cn(
                'p-2.5 rounded-md border',
                'bg-[var(--color-blocker-tint,var(--color-bg-base))]',
                'border-[var(--color-blocker-tint-bd,var(--color-hairline))]'
              )}
              data-testid={`attack-item-${index}`}
            >
              <p className="text-xs font-medium text-[var(--color-text-primary)]">
                {item.methodology}
              </p>
              <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                {item.attack}
              </p>

              {item.our_counter ? (
                <p
                  className="text-xs text-[var(--color-text-primary)] mt-1"
                  data-testid={`attack-counter-${index}`}
                >
                  Our counter: {item.our_counter}
                </p>
              ) : null}

              {item.counter_gap === true ? (
                <div className="mt-1.5" data-testid={`attack-gap-${index}`}>
                  <StateChip state="needs-human" text="COUNTER GAP" size="sm" />
                </div>
              ) : null}

              {(() => {
                const ev = item.evidence_needed ?? [];
                if (docDetection === 'ok' && ev.length === 0) {
                  return null;
                }
                if (docDetection === 'ok' && ev.length > 0) {
                  return (
                    <ul
                      className="mt-1 space-y-0.5"
                      data-testid={`attack-evidence-${index}`}
                    >
                      {ev.map((req, ri) => (
                        <li
                          key={ri}
                          className="text-xs text-[var(--color-text-secondary)]"
                        >
                          {req.display_name}{' '}
                          <span className="text-[var(--color-text-muted)]">
                            ({req.document_type})
                          </span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                return (
                  <p
                    className="text-xs text-[var(--color-text-muted)] mt-1"
                    data-testid={`attack-evidence-unavailable-${index}`}
                  >
                    Evidence detection unavailable
                  </p>
                );
              })()}

              {item.impact_if_collected ? (
                <p
                  className="text-xs text-[var(--color-text-muted)] mt-1"
                  data-testid={`attack-impact-${index}`}
                >
                  Impact if collected: {item.impact_if_collected}
                </p>
              ) : null}

              {(() => {
                const cs = item.collection_status;
                if (!cs) return null;
                const chipState =
                  cs.state === 'requested'
                    ? 'system-active'
                    : cs.state === 'not_requested'
                      ? 'dormant'
                      : 'needs-human';
                return (
                  <div className="mt-1.5" data-testid={`attack-collection-${index}`}>
                    <StateChip
                      state={chipState}
                      text={cs.state.replace(/_/g, ' ')}
                      size="sm"
                    />
                  </div>
                );
              })()}
            </div>
          ))
        ) : (
          <p
            className="text-sm text-[var(--color-text-secondary)] italic"
            data-testid="empty-state-message"
          >
            No active defense challenges
          </p>
        )}
      </div>
    </div>
  );
}

// Default export for convenience
export default {
  NextBestActionCard,
  CaseStrengthMeter,
  DefensePreviewCard,
};
